import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlugins, type CreatePluginData } from '../hooks/usePlugins'
import { useCreatePlugin } from '../hooks/usePlugins'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import { Plus, Package, Download, Lock, Unlock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'

export function PluginsPage() {
  const navigate = useNavigate()
  const { data: plugins, isLoading, error } = usePlugins()
  const createPlugin = useCreatePlugin()

  const [isCreateOpen, setIsCreateOpen] = useState(false)

  // Form state
  const [formData, setFormData] = useState<CreatePluginData>({
    slug: '',
    name: '',
    current_version: '1.0.0',
    type: 'plugin',
    requires_license: true,
    author: 'Jezweb',
  })

  const handleCreate = async () => {
    try {
      await createPlugin.mutateAsync(formData)
      toast.success('Plugin created successfully')
      setIsCreateOpen(false)
      setFormData({
        slug: '',
        name: '',
        current_version: '1.0.0',
        type: 'plugin',
        requires_license: true,
        author: 'Jezweb',
      })
    } catch (err) {
      toast.error('Failed to create plugin')
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-destructive">Failed to load plugins: {String(error)}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plugins</h1>
          <p className="text-muted-foreground">Manage WordPress plugins and themes</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Plugin</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Plugin</DialogTitle>
              <DialogDescription>
                Register a new plugin or theme on the update server.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4 md:space-y-4">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  placeholder="my-plugin"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="My Plugin"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version">Version</Label>
                <Input
                  id="version"
                  placeholder="1.0.0"
                  value={formData.current_version}
                  onChange={(e) => setFormData({ ...formData, current_version: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Requires License</Label>
                  <p className="text-xs text-muted-foreground">
                    Private plugin requiring license key
                  </p>
                </div>
                <Switch
                  checked={formData.requires_license}
                  onCheckedChange={(checked) => setFormData({ ...formData, requires_license: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={createPlugin.isPending}>
                {createPlugin.isPending ? 'Creating...' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className="p-4 pb-2 md:p-6 md:pb-2">
          <CardTitle>All Plugins</CardTitle>
          <CardDescription>
            {isLoading ? 'Loading...' : `${plugins?.length || 0} plugins registered`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : plugins?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No plugins yet</h3>
              <p className="text-muted-foreground">
                Create your first plugin to get started
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plugin</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>License</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plugins?.map((plugin) => (
                  <TableRow
                    key={plugin.slug}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/dashboard/plugins/${plugin.slug}`)}
                  >
                    <TableCell>
                      <div>
                        <p className="font-medium">{plugin.name}</p>
                        <p className="text-sm text-muted-foreground">{plugin.slug}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{plugin.current_version}</Badge>
                    </TableCell>
                    <TableCell>
                      {plugin.requires_license ? (
                        <div className="flex items-center text-amber-600">
                          <Lock className="mr-1 h-4 w-4" />
                          Required
                        </div>
                      ) : (
                        <div className="flex items-center text-green-600">
                          <Unlock className="mr-1 h-4 w-4" />
                          Free
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {plugin.last_updated
                        ? formatDistanceToNow(new Date(plugin.last_updated), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" asChild>
                        <a
                          href={`/api/update-server/plugins/${plugin.slug}/download?version=${plugin.current_version}`}
                          title={`Download ${plugin.name} v${plugin.current_version}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
